import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import Cookies from 'js-cookie'
import { baseDomain } from '../utils/api'

import { hasPageAccess, hasSurveyAccess as surveyAccess, canPerformAction as actionAccess, canWrite as writeAccess } from '../utils/accessControl'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(() => Cookies.get('auth_token') || null)
  const [loading, setLoading] = useState(true)

  // On mount, validate existing token or fall back to legacy cookies
  useEffect(() => {
    async function init() {
      if (token) {
        try {
          const res = await fetch(`${baseDomain}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (res.ok) {
            const data = await res.json()
            setUser(data)
            setLoading(false)
            return
          }
        } catch (e) {
          // Token invalid, fall through
        }
        // Token invalid — clear it
        Cookies.remove('auth_token')
        setToken(null)
      }

      setLoading(false)
    }
    init()
  }, [])

  const login = useCallback(async (username, password) => {
    const res = await fetch(`${baseDomain}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    })
    const data = await res.json()

    if (!data.authenticated) return { success: false }

    if (data.token) {
      setToken(data.token)
      Cookies.set('auth_token', data.token, { sameSite: 'strict' })
    }

    // Keep legacy cookies during transition
    Cookies.set('user', data.user)
    Cookies.set('authenticated', true)

    setUser(data.user)
    return { success: true, user: data.user }
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    setUser(null)
    Cookies.remove('auth_token')
    Cookies.remove('user')
    Cookies.remove('authenticated')
    window.location.href = '/'
  }, [])

  const hasPermission = useCallback(page => hasPageAccess(user, page), [user])
  const hasRole = useCallback((...roles) => !!user && roles.includes(user.role), [user])
  const hasSurveyAccess = useCallback((page, id) => surveyAccess(user, page, id), [user])
  const canPerformAction = useCallback((page, action) => actionAccess(user, page, action), [user])
  const canWrite = useCallback(page => writeAccess(user, page), [user])

  const isAuthenticated = !!user

  // Replace the stored auth token (used after server-side token rotation,
  // e.g. when the user changes their username and the backend mints a fresh
  // JWT carrying the new username in its payload).
  const setAuthToken = useCallback((newToken) => {
    if (!newToken) return
    setToken(newToken)
    Cookies.set('auth_token', newToken, { sameSite: 'strict' })
  }, [])

  // Re-fetch /me. Used after profile mutations (e.g. email change) so the UI
  // reflects pending_email without forcing a hard reload.
  const refreshUser = useCallback(async () => {
    if (!token) return null
    try {
      const res = await fetch(`${baseDomain}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setUser(data)
        Cookies.set('user', data)
        return data
      } else if (res.status === 401) {
        logout()
      }
    } catch (e) {
      // network blip; leave existing user state intact
    }
    return null
  }, [token, logout])

  useEffect(() => {
    if (!token) return
    const refresh = () => { refreshUser() }
    window.addEventListener('focus', refresh)
    const timer = window.setInterval(refresh, 30000)
    return () => { window.removeEventListener('focus', refresh); window.clearInterval(timer) }
  }, [token, refreshUser])

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshUser,
    setAuthToken,
    hasPermission,
    hasRole,
    hasSurveyAccess,
    canWrite,
    canPerformAction,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export default AuthContext
